import type { BasicEntry } from '../services/types/vacancy.types';
import { removeCommas } from './formatting';
import type {
  CustomerEntry,
  QualificationEntry,
  SkillEntry,
  TechnologyEntry,
} from '../services/types/vacancy.types';
import type { JDLocationRow } from './vacancyValidation';

export function buildEditVacancyFormData(
  basicData: BasicEntry,
  customers: CustomerEntry[],
  qualifications: QualificationEntry[],
  primarySkills: SkillEntry[],
  secondarySkills: SkillEntry[],
  primaryTechnologies: TechnologyEntry[],
  secondaryTechnologies: TechnologyEntry[],
  initialIsActive: boolean,
  initialIsVisible: boolean,
): FormData {
  const formData = new FormData();

  formData.append('vacancyType', basicData.vacancyType);
  formData.append('vacancyTitle', basicData.vacancyTitle);
  formData.append('vacancyCode', basicData.vacancyCode);
  formData.append('minimumIndustryExperience', basicData.minIndustryExperience);
  formData.append('vacancyStartDate', basicData.startDate || '');
  formData.append('vacancyEndDate', basicData.endDate || '');
  formData.append('vacancyDueDate', basicData.dueDate || '');
  formData.append('vacancyReason', basicData.vacancyReason);
  formData.append('designation', basicData.designation?.value || '0');
  formData.append('isActive', basicData.isActive.toString());

  if (basicData.isActive !== initialIsActive) {
    formData.append('isActive', basicData.isActive ? '1' : '0');
  }

  if (basicData.isVisible !== initialIsVisible) {
    formData.append('isVisible', basicData.isVisible ? '1' : '0');
  }

  const customersData: Array<{
    id: string;
    numberOfVacancies: number;
    revenuePerResource: string;
  }> = [];
  customers.forEach((customer) => {
    if (customer.id && customer.numberOfVacancies) {
      customersData.push({
        id: customer.id,
        numberOfVacancies: parseInt(customer.numberOfVacancies, 10),
        revenuePerResource: customer.revenuePerResource,
      });
    }
  });
  formData.append('customers', JSON.stringify(customersData));

  const qualificationsData: Array<{ id: string | undefined }> = [];
  qualifications.forEach((qualification) => {
    if (qualification.qualification && qualification.type) {
      qualificationsData.push({
        id: qualification.qualification?.value,
      });
    }
  });
  formData.append('qualifications', JSON.stringify(qualificationsData));

  const primarySkillsData: Array<{ id: string | undefined; yearsOfExperience: string }> = [];
  primarySkills.forEach((skill) => {
    if (skill.skill && skill.years) {
      primarySkillsData.push({
        id: skill.skill?.value,
        yearsOfExperience: skill.years,
      });
    }
  });
  formData.append('primarySkills', JSON.stringify(primarySkillsData));

  const secondarySkillsData: Array<{ id: string | undefined; yearsOfExperience: string }> = [];
  secondarySkills.forEach((skill) => {
    if (skill.skill && skill.years) {
      secondarySkillsData.push({
        id: skill.skill?.value,
        yearsOfExperience: skill.years,
      });
    }
  });
  formData.append('secondarySkills', JSON.stringify(secondarySkillsData));

  const primaryTechnologiesData: Array<{ id: string | undefined; yearsOfExperience: string }> = [];
  primaryTechnologies.forEach((technology) => {
    if (technology.technology && technology.years) {
      primaryTechnologiesData.push({
        id: technology.technology?.value,
        yearsOfExperience: technology.years,
      });
    }
  });
  formData.append('primaryTechnologies', JSON.stringify(primaryTechnologiesData));

  const secondaryTechnologiesData: Array<{ id: string | undefined; yearsOfExperience: string }> = [];
  secondaryTechnologies.forEach((technology) => {
    if (technology.technology && technology.years) {
      secondaryTechnologiesData.push({
        id: technology.technology?.value,
        yearsOfExperience: technology.years,
      });
    }
  });
  formData.append('secondaryTechnologies', JSON.stringify(secondaryTechnologiesData));

  return formData;
}

export function buildAddVacancyPayload(
  basicData: BasicEntry,
  customers: CustomerEntry[],
  qualifications: QualificationEntry[],
  primarySkills: SkillEntry[],
  secondarySkills: SkillEntry[],
  primaryTechnologies: TechnologyEntry[],
  secondaryTechnologies: TechnologyEntry[],
): Record<string, unknown> {
  const formData: Record<string, unknown> = {};

  formData.vacancyType = basicData.vacancyType;
  formData.vacancyTitle = basicData.vacancyTitle;
  formData.vacancyCode = basicData.vacancyCode;
  formData.minimumIndustryExperience = parseInt(basicData.minIndustryExperience, 10);
  formData.vacancyStartDate = basicData.startDate || '';
  formData.vacancyEndDate = basicData.endDate || '';
  formData.vacancyDueDate = basicData.dueDate || '';
  formData.vacancyReason = basicData.vacancyReason;
  formData.designation = parseInt(basicData.designation?.value || '', 10);
  formData.isActive = basicData.isActive;
  formData.isVisible = basicData.isVisible;

  type CustomerPayload = { id: string; numberOfVacancies: number; revenuePerResource: string };
  formData.customers = [] as CustomerPayload[];
  customers.forEach((customer, index) => {
    if (
      customer.id &&
      customer.numberOfVacancies &&
      customer.revenuePerResource
    ) {
      (formData.customers as CustomerPayload[])[index] = {
        id: customer.id,
        numberOfVacancies: parseInt(customer.numberOfVacancies, 10),
        revenuePerResource: customer.revenuePerResource,
      };
    }
  });

  type QualPayload = { qualificationID: number; type: string };
  formData.qualifications = [] as QualPayload[];
  qualifications.forEach((qualification, index) => {
    if (qualification.qualification && qualification.type) {
      (formData.qualifications as QualPayload[])[index] = {
        qualificationID: parseInt(qualification.qualification?.value || '', 10),
        type: qualification.type,
      };
    }
  });

  type SkillPayload = { skillID: number; yearsOfExperience: number };
  formData.primarySkills = [] as SkillPayload[];
  primarySkills.forEach((skill, index) => {
    if (skill.skill && skill.years) {
      (formData.primarySkills as SkillPayload[])[index] = {
        skillID: parseInt(skill.skill?.value || '', 10),
        yearsOfExperience: parseInt(skill.years, 10),
      };
    }
  });

  formData.secondarySkills = [] as SkillPayload[];
  secondarySkills.forEach((skill, index) => {
    if (skill.skill && skill.years) {
      (formData.secondarySkills as SkillPayload[])[index] = {
        skillID: parseInt(skill.skill?.value || '', 10),
        yearsOfExperience: parseInt(skill.years, 10),
      };
    }
  });

  type TechPayload = { technologyID: number; yearsOfExperience: number };
  formData.primaryTechnologies = [] as TechPayload[];
  primaryTechnologies.forEach((technology, index) => {
    if (technology.technology && technology.years) {
      (formData.primaryTechnologies as TechPayload[])[index] = {
        technologyID: parseInt(technology.technology?.value || '', 10),
        yearsOfExperience: parseInt(technology.years, 10),
      };
    }
  });

  formData.secondaryTechnologies = [] as TechPayload[];
  secondaryTechnologies.forEach((technology, index) => {
    if (technology.technology && technology.years) {
      (formData.secondaryTechnologies as TechPayload[])[index] = {
        technologyID: parseInt(technology.technology?.value || '', 10),
        yearsOfExperience: parseInt(technology.years, 10),
      };
    }
  });

  return formData;
}

export function buildJDFormData(
  aboutClient: string,
  aboutEyepax: string,
  jobDescription: string,
  description: string,
  locations: JDLocationRow[],
  isGenerateJD: boolean,
): FormData {
  const formData = new FormData();

  formData.append('vacancyAboutClient', aboutClient);
  formData.append('vacancyAboutEyepax', aboutEyepax);
  formData.append('jobDescription', jobDescription);
  formData.append('description', description);
  formData.append('isGenerateJD', isGenerateJD ? 'true' : 'false');

  const locationsData: Array<{
    city: string | null;
    country: string | undefined;
    budgetAmount: number | null;
    budgetCurrency: string;
  }> = [];
  locations.forEach((location) => {
    if (location?.country?.value) {
      const budgetAmountStr = removeCommas(location.budgetAmount || '');
      const budgetAmount =
        budgetAmountStr && budgetAmountStr.trim() !== ''
          ? parseFloat(budgetAmountStr)
          : null;
      const roundedAmount =
        budgetAmount && !isNaN(budgetAmount) ? Math.round(budgetAmount * 100) / 100 : null;
      locationsData.push({
        city: location?.city?.value ?? null,
        country: location?.country?.value,
        budgetAmount: roundedAmount,
        budgetCurrency: location.budgetCurrency || 'USD',
      });
    }
  });
  formData.append('locations', JSON.stringify(locationsData));

  return formData;
}

export function buildUploadFormData(
  vacancyUrl: string,
  tinyUrl: string,
  socialMedia: string,
  tags: string,
  publishedDate: string,
  approvalStatus: number,
  initialApprovalStatus: number,
  linkedinImage: File | string | null,
  facebookImage: File | string | null,
  instagramImage: File | string | null,
  includeApprovalStatus: boolean = true,
): FormData {
  const formData = new FormData();

  formData.append('vacancyUrl', vacancyUrl);
  formData.append('vacancyTinyUrl', tinyUrl);
  formData.append('socialMediaDesc', socialMedia);
  formData.append('socialMediaTags', tags);
  formData.append('publishedDate', publishedDate);

  if (includeApprovalStatus && approvalStatus !== initialApprovalStatus) {
    formData.append('approvalStatus', approvalStatus.toString());
  }

  if (linkedinImage) {
    formData.append('imageLinkedIn', linkedinImage);
  }
  if (facebookImage) {
    formData.append('imageFB', facebookImage);
  }
  if (instagramImage) {
    formData.append('imageInstagram', instagramImage);
  }

  return formData;
}
